import React from 'react';
import Layout from '../components/Layout';
import { Button, Card } from '../design-system';

const About: React.FC = () => {
  return (
    <Layout>
      <div className="space-y-16">
        {/* Company Overview */}
        <section className="grid items-start gap-12 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <h2 className="text-3xl font-bold text-white">Our Story</h2>
            <div className="space-y-4 text-lg leading-relaxed text-white/80">
              <p>
                Founded in 2023, Incupie Technologies Private Limited emerged from a simple yet powerful vision:
                to democratize access to cutting-edge AI technologies and make them as easy to use as everyday software.
              </p>
              <p>
                We recognized that while AI was advancing rapidly, there was a significant gap between
                breakthrough research and practical, accessible applications for businesses and individuals.
                Incupie was created to bridge this gap.
              </p>
              <p>
                Today, we're proud to offer a comprehensive platform that enables users to run sophisticated
                AI models locally, ensuring privacy, control, and unlimited creative possibilities.
              </p>
            </div>
          </div>
          <div className="space-y-6">
            <Card hover centered>
              <div className="mb-2 text-3xl font-bold text-primary-400">Cleaner</div>
              <div className="text-white/80">Datasets</div>
            </Card>
            <Card hover centered>
              <div className="mb-2 text-3xl font-bold text-primary-400">4x Faster</div>
              <div className="text-white/80">Models</div>
            </Card>
            <Card hover centered>
              <div className="mb-2 text-3xl font-bold text-primary-400">99.7%</div>
              <div className="text-white/80">Model/Data Compression</div>
            </Card>
          </div>
        </section>

        {/* Leadership */}
        <section className="space-y-8">
          <div className="text-center">
            <h2 className="mb-4 text-3xl font-bold text-white">Team Incupie</h2>
            <p className="max-w-3xl mx-auto text-lg text-white/80">
              Our diverse team of experts combines deep technical knowledge with a passion for making AI accessible to everyone.
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-2 xl:grid-cols-3">
            {/* Founder */}
            <Card hover padding="lg" centered>
              <div className="space-y-4">
                <div className="w-32 h-32 mx-auto overflow-hidden rounded-full">
                  <img
                    src="/profiles/Harsh.jpg"
                    alt="Harsh Kumar - Founder & CEO"
                    className="object-cover w-full h-full"
                  />
                </div>
                <div className="text-center">
                  <h3 className="mb-1 text-xl font-bold text-white">Harsh Kumar</h3>
                  <p className="mb-3 font-semibold text-primary-400">Founder & CEO</p>
                  <p className="text-sm leading-relaxed text-white/80">
                    Visionary founder driving the future of AI innovation. Innovative genius behind Incupie's core
                    technology that empowers organizations and users worldwide.
                  </p>
                  <div className="flex justify-center gap-3 mt-4">
                    <a href="https://x.com/theharshon" target="_blank" rel="noopener noreferrer" className="px-3 py-2 text-sm transition-all rounded-lg bg-primary-500/20 border border-primary-500/30 text-primary-400 hover:bg-primary-500/30 hover:text-primary-300">
                      X
                    </a>
                    <a href="https://www.linkedin.com/in/theharshon/" target="_blank" rel="noopener noreferrer" className="px-3 py-2 text-sm transition-all rounded-lg bg-primary-500/20 border border-primary-500/30 text-primary-400 hover:bg-primary-500/30 hover:text-primary-300">
                      LinkedIn
                    </a>
                  </div>
                </div>
              </div>
            </Card>

            {/* Co-Founder */}
            <Card hover padding="lg" centered>
              <div className="space-y-4">
                <div className="w-32 h-32 mx-auto overflow-hidden rounded-full">
                  <img
                    src="/profiles/Bhaskar.jpg"
                    alt="Bhaskar Chouhan - Co-Founder & COO"
                    className="object-cover w-full h-full"
                  />
                </div>
                <div className="text-center">
                  <h3 className="mb-1 text-xl font-bold text-white">Bhaskar Chouhan</h3>
                  <p className="mb-3 font-semibold text-primary-400">Co-Founder & COO</p>
                  <p className="text-sm leading-relaxed text-white/80">
                    Strategic leader and co-visionary behind Incupie's mission. Works closely with the founder
                    to shape the company's direction and drive innovation at scale.
                  </p>
                  <div className="flex justify-center gap-3 mt-4">
                    <a href="https://x.com/vaskarchouhan" target="_blank" rel="noopener noreferrer" className="px-3 py-2 text-sm transition-all rounded-lg bg-primary-500/20 border border-primary-500/30 text-primary-400 hover:bg-primary-500/30 hover:text-primary-300">
                      X
                    </a>
                    <a href="https://www.linkedin.com/in/realbhaskarchouhan/" target="_blank" rel="noopener noreferrer" className="px-3 py-2 text-sm transition-all rounded-lg bg-primary-500/20 border border-primary-500/30 text-primary-400 hover:bg-primary-500/30 hover:text-primary-300">
                      LinkedIn
                    </a>
                  </div>
                </div>
              </div>
            </Card>

            {/* Tech Lead */}
            <Card hover padding="lg" centered>
              <div className="space-y-4">
                <div className="w-32 h-32 mx-auto overflow-hidden rounded-full">
                  <img
                    src="/profiles/Sanjay.jpg"
                    alt="Sanjay Thakur - Lead System Architect"
                    className="object-cover w-full h-full"
                  />
                </div>
                <div className="text-center">
                  <h3 className="mb-1 text-xl font-bold text-white">Sanjay Thakur</h3>
                  <p className="mb-3 font-semibold text-primary-400">Lead System Architect</p>
                  <p className="text-sm leading-relaxed text-white/80">
                    Technical expert responsible for our platform architecture and AI model integration.
                    Leads the development of cutting-edge AI technologies and infrastructure.
                  </p>
                  <div className="flex justify-center gap-3 mt-4">
                    <a href="https://x.com/SanjayInnovates" target="_blank" rel="noopener noreferrer" className="px-3 py-2 text-sm transition-all rounded-lg bg-primary-500/20 border border-primary-500/30 text-primary-400 hover:bg-primary-500/30 hover:text-primary-300">
                      X
                    </a>
                    <a href="https://www.linkedin.com/in/sanjay219/" target="_blank" rel="noopener noreferrer" className="px-3 py-2 text-sm transition-all rounded-lg bg-primary-500/20 border border-primary-500/30 text-primary-400 hover:bg-primary-500/30 hover:text-primary-300">
                      LinkedIn
                    </a>
                  </div>
                </div>
              </div>
            </Card>

            {/* Marketing Lead */}
            <Card hover padding="lg" centered>
              <div className="space-y-4">
                <div className="w-32 h-32 mx-auto overflow-hidden rounded-full">
                  <img
                    src="/profiles/Yash.jpg"
                    alt="Yash Rana - CMO"
                    className="object-cover w-full h-full"
                  />
                </div>
                <div className="text-center">
                  <h3 className="mb-1 text-xl font-bold text-white">Yash Rana</h3>
                  <p className="mb-3 font-semibold text-primary-400">CMO</p>
                  <p className="text-sm leading-relaxed text-white/80">
                    Drives marketing strategy and brand awareness. Ensures Incupie's message reaches
                    the right audience and builds meaningful connections with our community.
                  </p>
                  <div className="flex justify-center gap-3 mt-4">
                    <a href="https://x.com/yashranaz5" target="_blank" rel="noopener noreferrer" className="px-3 py-2 text-sm transition-all rounded-lg bg-primary-500/20 border border-primary-500/30 text-primary-400 hover:bg-primary-500/30 hover:text-primary-300">
                      X
                    </a>
                    <a href="https://www.linkedin.com/in/yashranaz5/" target="_blank" rel="noopener noreferrer" className="px-3 py-2 text-sm transition-all rounded-lg bg-primary-500/20 border border-primary-500/30 text-primary-400 hover:bg-primary-500/30 hover:text-primary-300">
                      LinkedIn
                    </a>
                  </div>
                </div>
              </div>
            </Card>

            {/* Design Lead */}
            <Card hover padding="lg" centered>
              <div className="space-y-4">
                <div className="w-32 h-32 mx-auto overflow-hidden rounded-full">
                  <img
                    src="/profiles/Abhinav.jpg"
                    alt="Abhinav Singh - Graphic Designer"
                    className="object-cover w-full h-full"
                  />
                </div>
                <div className="text-center">
                  <h3 className="mb-1 text-xl font-bold text-white">Abhinav Singh</h3>
                  <p className="mb-3 font-semibold text-primary-400">Graphic Designer</p>
                  <p className="text-sm leading-relaxed text-white/80">
                    Graphic design expert responsible for our visual identity and user interface.
                    Creates beautiful, intuitive designs that make our platform accessible to everyone.
                  </p>
                  <div className="flex justify-center gap-3 mt-4">
                    <a href="https://x.com/browndevv" target="_blank" rel="noopener noreferrer" className="px-3 py-2 text-sm transition-all rounded-lg bg-primary-500/20 border border-primary-500/30 text-primary-400 hover:bg-primary-500/30 hover:text-primary-300">
                      X
                    </a>
                    <a href="https://www.linkedin.com/in/browndevv/" target="_blank" rel="noopener noreferrer" className="px-3 py-2 text-sm transition-all rounded-lg bg-primary-500/20 border border-primary-500/30 text-primary-400 hover:bg-primary-500/30 hover:text-primary-300">
                      LinkedIn
                    </a>
                  </div>
                </div>
              </div>
            </Card>

            {/* Join Our Team */}
            <Card hover padding="lg" centered className="border border-white/10">
              <div className="space-y-4">
                <div className="flex items-center justify-center w-32 h-32 mx-auto border-2 border-dashed rounded-full bg-gray-700/30 border-primary-500/40">
                  <div className="text-center text-primary-400">
                    <div className="mb-1 text-2xl">🌟</div>
                    <div className="text-xs">Be Part of It</div>
                  </div>
                </div>
                <div className="text-center">
                  <h3 className="mb-1 text-xl font-bold text-white">Curious About AI?</h3>
                  <p className="mb-3 font-semibold text-primary-400">You Can Be Part of It</p>
                  <p className="mb-4 text-sm leading-relaxed text-white/80">
                    Interested in the future of artificial intelligence? Whether you're a developer,
                    creator, or simply curious about AI's potential, join us in shaping tomorrow.
                  </p>
                  <Button as="a" href="/contact" variant="secondary" size="sm" className="text-xs">
                    Join Us
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </section>

        {/* Vision & Mission */}        <section className="grid gap-8 md:grid-cols-2">
          <Card hover padding="lg" centered>
            <div className="mb-6 text-4xl">🎯</div>
            <h3 className="mb-4 text-2xl font-bold text-white">Our Mission</h3>
            <p className="leading-relaxed text-white/80">
              To build transformative AI systems that empower organizations and individuals to work smarter and faster than imagination.
            </p>
          </Card>

          <Card hover padding="lg" centered>
            <div className="mb-6 text-4xl">🚀</div>
            <h3 className="mb-4 text-2xl font-bold text-white">Our Vision</h3>
            <p className="leading-relaxed text-white/80">
              To be the driving force behind a new era of work, where Incupie seamlessly enhances human potential, unlocks innovation at scale, and redefines productivity across industries.
            </p>
          </Card>
        </section>

        {/* Values */}
        <section className="space-y-8">
          <div className="text-center">
            <h2 className="mb-4 text-3xl font-bold text-white">Our Values</h2>
            <p className="max-w-3xl mx-auto text-lg text-white/80">
              These principles guide everything we do and help us stay focused on what matters most.
            </p>
          </div>
          <div className="grid gap-8 md:grid-cols-3">
            <Card hover centered>
              <div className="mb-4 text-3xl">🔒</div>
              <h3 className="mb-3 text-xl font-bold text-white">Privacy First</h3>
              <p className="text-white/80">
                Your data stays yours. We prioritize local processing and user control over their AI workflows.
              </p>
            </Card>

            <Card hover centered>
              <div className="mb-4 text-3xl">⚡</div>
              <h3 className="mb-3 text-xl font-bold text-white">Innovation</h3>
              <p className="text-white/80">
                We stay at the forefront of AI research to bring you the latest and most powerful models.
              </p>
            </Card>

            <Card hover centered>
              <div className="mb-4 text-3xl">🤝</div>
              <h3 className="mb-3 text-xl font-bold text-white">Community</h3>
              <p className="text-white/80">
                We believe in building together and supporting our users every step of their AI journey.
              </p>
            </Card>
          </div>
        </section>        {/* Call to Action */}
        <Card padding="xl" centered>
          <h2 className="mb-4 text-3xl font-bold text-white">Join Our Journey</h2>
          <p className="max-w-2xl mx-auto mb-8 text-lg text-white/80">
            Be part of the AI revolution. Whether you're a developer, creator, or business owner,
            we're here to help you unlock the full potential of artificial intelligence.
          </p>          <div className="flex flex-col justify-center gap-4 sm:flex-row">

            <Button as="a" href="/contact" variant="secondary">
              Get in Touch
            </Button>
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default About;
